import { useState } from 'react';
import { useTheme } from '../lib/theme-provider';
import { ThemedCard } from './ThemedCard';
import { ThemedButton } from './ThemedButton';
import { FileText, X, Save, Trash2 } from 'lucide-react';

interface GameNotesModalProps {
  isOpen: boolean;
  initialNotes?: string;
  onClose: () => void;
  onSave: (notes: string) => void;
}

export default function GameNotesModal({
  isOpen,
  initialNotes = '',
  onClose,
  onSave,
}: GameNotesModalProps) {
  const { theme } = useTheme();
  const [notes, setNotes] = useState(initialNotes);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(notes);
  };

  const handleClear = () => {
    setNotes('');
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }}
    >
      <ThemedCard className="w-full max-w-md p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div
            className="flex items-center gap-2"
            style={{ color: theme.colors['accent-gold'] }}
          >
            <FileText size={24} />
            <h2
              className="text-xl font-bold"
              style={{ color: theme.colors['text-primary'] }}
            >
              游戏笔记
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ backgroundColor: theme.colors['bg-primary'] }}
          >
            <X size={20} style={{ color: theme.colors['text-secondary'] }} />
          </button>
        </div>

        {/* 说明 */}
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{
            backgroundColor: `${theme.colors['accent-gold']}10`,
            border: `1px solid ${theme.colors['accent-gold']}30`,
            color: theme.colors['text-secondary'],
          }}
        >
          记录你的推理和观察，仅自己可见
        </div>

        {/* 文本输入区 */}
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="在这里记录你的游戏笔记...\n\n例如：\n- #1 玩家行为可疑\n- #3 可能是梅林\n- 第2轮任务失败的原因..."
          className="w-full h-48 p-4 rounded-xl resize-none text-sm mb-4"
          style={{
            backgroundColor: theme.colors['bg-primary'],
            border: `1px solid ${theme.colors['border-secondary']}`,
            color: theme.colors['text-primary'],
          }}
        />

        {/* 快捷标签 */}
        <div className="flex flex-wrap gap-2 mb-4">
          {['梅林', '派西维尔', '刺客', '莫甘娜', '忠臣', '怀疑', '确认'].map(
            (tag) => (
              <button
                key={tag}
                onClick={() => setNotes(notes + (notes ? ' ' : '') + tag)}
                className="px-3 py-1 rounded-full text-xs transition-colors"
                style={{
                  backgroundColor: theme.colors['bg-primary'],
                  border: `1px solid ${theme.colors['border-secondary']}`,
                  color: theme.colors['text-secondary'],
                }}
              >
                {tag}
              </button>
            )
          )}
        </div>

        {/* 操作按钮 */}
        <div className="flex gap-3">
          <ThemedButton
            variant="secondary"
            onClick={handleClear}
            className="flex-1"
          >
            <Trash2 size={18} className="mr-1" />
            清空
          </ThemedButton>
          <ThemedButton onClick={handleSave} className="flex-1">
            <Save size={18} className="mr-1" />
            保存
          </ThemedButton>
        </div>
      </ThemedCard>
    </div>
  );
}
